// Sentry must init before anything else — catches errors during module evaluation
import './lib/sentry'

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Sentry } from './lib/sentry'
import { ThemeProvider } from './hooks/useTheme'
import App from './App.tsx'

// Self-hosted fonts via Fontsource
import '@fontsource/dm-sans/400.css'
import '@fontsource/dm-sans/500.css'
import '@fontsource/dm-sans/600.css'
import '@fontsource/dm-sans/700.css'
import '@fontsource/jetbrains-mono/400.css'
import '@fontsource/jetbrains-mono/500.css'
import '@fontsource/sora/700.css'

import './index.css'

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={sentryFallback}>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)

function sentryFallback({ error, resetError }: { error: unknown; componentStack: string; eventId: string; resetError: () => void }) {
  const message = error instanceof Error ? error.message : String(error)
  const stack = error instanceof Error ? error.stack : undefined

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '2rem',
      fontFamily: '"DM Sans", system-ui, sans-serif',
      backgroundColor: '#f8f6f3',
      color: '#1a2744',
    }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.75rem' }}>
        Something went wrong
      </h1>
      <p style={{ maxWidth: '28rem', textAlign: 'center', color: '#64748b', marginBottom: '1.5rem' }}>
        An unexpected error occurred. Your data is safe. Try refreshing the page, or
        contact support if the problem persists.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1.25rem',
            backgroundColor: '#1a2744',
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '0.875rem',
          }}
        >
          Refresh page
        </button>
        <button
          onClick={resetError}
          style={{
            padding: '0.5rem 1.25rem',
            backgroundColor: 'transparent',
            color: '#1a2744',
            border: '1px solid #1a2744',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '0.875rem',
          }}
        >
          Try again
        </button>
      </div>
      {import.meta.env.DEV && (
        <pre style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#1e293b',
          color: '#f87171',
          borderRadius: '0.375rem',
          fontSize: '0.75rem',
          maxWidth: '48rem',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}>
          {message}
          {stack && '\n' + stack}
        </pre>
      )}
    </div>
  )
}
